import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as service from "../service/admissionApplicationsService";
import type { AdmissionApplicationsState, ApplicationStatus, ApproveApplicationPayload } from "../types";
import i18n from "@/i18n";

export const fetchApplications = createAsyncThunk(
  "admissionApplications/fetch",
  async ({ institutionId, status, page, limit }: { institutionId: number; status?: ApplicationStatus; page?: number; limit?: number }) => {
    return await service.fetchApplications(institutionId, { status, page, limit });
  }
);

export const approveApplication = createAsyncThunk(
  "admissionApplications/approve",
  async (
    { institutionId, applicationId, payload }: { institutionId: number; applicationId: number; payload: ApproveApplicationPayload },
    { dispatch }
  ) => {
    const result = await service.approveApplication(institutionId, applicationId, payload);
    await dispatch(fetchApplications({ institutionId }));
    return result;
  }
);

export const rejectApplication = createAsyncThunk(
  "admissionApplications/reject",
  async (
    { institutionId, applicationId, rejectionReason }: { institutionId: number; applicationId: number; rejectionReason?: string },
    { dispatch }
  ) => {
    await service.rejectApplication(institutionId, applicationId, rejectionReason);
    await dispatch(fetchApplications({ institutionId }));
  }
);

const initialState: AdmissionApplicationsState = {
  applications: [],
  pagination: { page: 1, limit: 20, total: 0, totalPages: 1 },
  loading: false,
  submitting: false,
  error: null,
  lastResult: null,
};

const admissionApplicationsSlice = createSlice({
  name: "admissionApplications",
  initialState,
  reducers: {
    clearApplicationResult(state) {
      state.lastResult = null;
    },
    clearApplicationError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchApplications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.loading = false;
        state.applications = action.payload.applications;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchApplications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? i18n.t("admissions:admissionApplicationsSlicetsMsgs.failedToLoadAdmissionRequests");
      })
      .addCase(approveApplication.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(approveApplication.fulfilled, (state, action) => {
        state.submitting = false;
        state.lastResult = action.payload;
      })
      .addCase(approveApplication.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.error.message ?? i18n.t("admissions:admissionApplicationsSlicetsMsgs.failedToApproveApplication");
      })
      .addCase(rejectApplication.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(rejectApplication.fulfilled, (state) => {
        state.submitting = false;
      })
      .addCase(rejectApplication.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.error.message ?? i18n.t("admissions:admissionApplicationsSlicetsMsgs.failedToRejectApplication");
      });
  },
});

export const { clearApplicationResult, clearApplicationError } = admissionApplicationsSlice.actions;
export default admissionApplicationsSlice.reducer;
